import { Component, Input, Inject, NgZone, OnChanges, OnDestroy, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5Local from '@amcharts/amcharts5/locales/es_ES';
import { ChartLoadingComponent } from '../chart-loading/chart-loading.component';
import { HEX_COLOR_PALETTE } from '../utils/color-palette';
import { ChartValueType, valueFormatter } from '../utils/chart-utils';

export interface ChartBarSeries {
  field: string;
  name: string;
  type?: ChartValueType;                         // formato del tooltip y la etiqueta de valor de esta serie
}

export interface ChartBarGroupBy {
  key: string;
  valueField: string;
  type?: ChartValueType;
}

export interface ChartBarConfig {
  categoryField: string;
  bars?: ChartBarSeries[];                       // una entrada = 1 serie; varias = barras agrupadas — se omite si se usa groupBy
  groupBy?: ChartBarGroupBy;                     // series dinámicas: una por valor único de esa columna
  showLegend?: boolean;                          // default: se muestra si hay más de 1 serie
  type?: ChartValueType;                         // formato de los labels del eje X (valor)
  barHeight?: number;                            // alto en px por fila de categoría (se multiplica por cant. de bars)
  maxHeight?: number;                            // alto máx. del wrapper con scroll
  title?: string;
  col?: number;                                  // md:col-{n} en ChartBarMultipleComponent — 1 a 12, default 12
}

/** Esto es para armar un card, no hace ningun efecto en el componente */
export interface ChartBarCardConfig {
  title: string;
  sub?: string;
  lastUpdated?: string;
  chart: ChartBarConfig;
}

@Component({
  selector: 'app-chart-bar',
  standalone: true,
  imports: [CommonModule, ChartLoadingComponent],
  styles: `
    :host {
      display: block;
      height: 100%;
    }

    .bar-canvas {
      width: 100%;
      min-height: 200px;
      min-width: 350px;
    }
  `,
  template: `
    <app-chart-loading *ngIf="isLoading; else canvas"></app-chart-loading>
    <ng-template #canvas>
      <div style="overflow-x: auto"
           [style.maxHeight.px]="config.maxHeight"
           [style.overflowY]="config.maxHeight ? 'auto' : null">
        <div [id]="chartId" class="bar-canvas"></div>
      </div>
    </ng-template>
  `
})
export class ChartBarComponent implements OnChanges, OnDestroy {

  chartId!: string;
  @Input() config!: ChartBarConfig;
  @Input() data: any[] = [];

  isLoading = true;

  private root?: am5.Root;
  private yAxis?: am5xy.CategoryAxis<am5xy.AxisRendererY>;
  private barSeriesList: am5xy.ColumnSeries[] = [];
  private resolvedBars: ChartBarSeries[] = [];
  private resolvedData: any[] = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.chartId = `chart-bar-${crypto.randomUUID()}`;
  }

  ngOnChanges(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config || !this.data?.length) return;

    const resolved = this.resolve();
    this.resolvedBars = resolved.bars;
    this.resolvedData = resolved.data;

    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();

    this.setDynamicHeight(this.resolvedData.length);
    const reversed = [...this.resolvedData].reverse();
    this.yAxis?.data.setAll(reversed);
    this.barSeriesList.forEach(s => s.data.setAll(reversed));
  }

  /** series estáticas si vienen declaradas; si viene groupBy, se derivan de los valores únicos de esa columna */
  private resolve(): { bars: ChartBarSeries[]; data: any[] } {
    if (!this.config.groupBy) return { bars: this.config.bars ?? [], data: this.data };

    const { key, valueField, type } = this.config.groupBy;
    const categoryField = this.config.categoryField;
    const rowsByCategory = new Map<string, any>();
    const groupValues = new Set<string>();
    this.data.forEach(row => {
      const category = row[categoryField];
      const groupValue = row[key];
      groupValues.add(groupValue);
      if (!rowsByCategory.has(category)) rowsByCategory.set(category, { [categoryField]: category });
      rowsByCategory.get(category)[groupValue] = row[valueField];
    });
    const bars: ChartBarSeries[] = [...groupValues].map(name => ({ field: name, name, type }));
    return { bars, data: [...rowsByCategory.values()] };
  }

  private initChart(): void {
    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.chartId);
      root.setThemes([am5themes_Animated.new(root)]);
      if (root._logo) root._logo.dispose();
      root.locale = am5Local;
      this.root = root;

      const textColor  = am5.color(0xd9e8ff);
      const mutedColor = am5.color(0x9db8d7);
      const gridColor  = am5.color(0x33506f);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          layout: root.verticalLayout,
          panX: false,
          panY: false,
          paddingLeft: 0,
        })
      );

      // Y axis — categories
      const yAxis: am5xy.CategoryAxis<am5xy.AxisRendererY> = chart.yAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: this.config.categoryField,
          renderer: am5xy.AxisRendererY.new(root, { minGridDistance: 18 }),
        })
      );
      yAxis.get('renderer').labels.template.setAll({
        fill: mutedColor,
        fontSize: 11,
        maxWidth: 140,
        oversizedBehavior: 'truncate',
        tooltipText: '{category}',
      });
      yAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });
      this.yAxis = yAxis;

      // X axis — values
      const xAxis = chart.xAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererX.new(root, {}),
        })
      );
      xAxis.get('renderer').labels.template.setAll({ fill: mutedColor, fontSize: 11 });
      xAxis.get('renderer').grid.template.setAll({ stroke: gridColor, strokeOpacity: 0.35 });
      const axisType = this.config.type;
      xAxis.get('renderer').labels.template.adapters.add('text', (_text, target) => {
        const value = (target.dataItem as any)?.get('value') as number | undefined;
        return value != null ? valueFormatter(axisType, value) : _text;
      });

      // Bar series
      this.resolvedBars.forEach((bar, index) => {
        const barColor = am5.color(HEX_COLOR_PALETTE[index % HEX_COLOR_PALETTE.length]);

        const tooltip = am5.Tooltip.new(root, {
          labelText: `[bold #fff]{categoryY}: {valueX}`,
        });
        tooltip.label.adapters.add('text', (_text, target) => {
          const value = (target.dataItem as any)?.get('valueX') as number | undefined;
          const cat = (target.dataItem as any)?.get('categoryY') as string | undefined;
          return value != null ? `[bold #fff]${cat ?? ''}: ${valueFormatter(bar.type, value)}` : _text;
        });

        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: bar.name,
            xAxis,
            yAxis,
            categoryYField: this.config.categoryField,
            valueXField: bar.field,
            sequencedInterpolation: true,
            tooltip,
          })
        );

        series.columns.template.setAll({
          fill: barColor,
          strokeOpacity: 0,
          height: am5.percent(80),
          cornerRadiusTR: 6,
          cornerRadiusBR: 6,
        });
        series.columns.template.states.create('hover', { fillOpacity: 0.8 });

        series.bullets.push(() => {
          const label = am5.Label.new(root, {
            text: `[bold]{${bar.field}}`,
            fill: textColor,
            centerY: am5.p50,
            populateText: true,
            fontSize: 10,
            paddingRight: 5,
          });
          label.adapters.add('text', (_text, target) => {
            const value = (target.dataItem as any)?.get('valueX') as number | undefined;
            return value != null ? `[bold]${valueFormatter(bar.type, value)}` : _text;
          });
          return am5.Bullet.new(root, { locationX: 0, locationY: 0.5, sprite: label });
        });

        series.appear(500);
        this.barSeriesList.push(series);
      });

      // Legend (shown by default when there is more than one series)
      const showLegend = this.config.showLegend ?? this.resolvedBars.length > 1;
      if (showLegend) {
        const legend = chart.children.push(
          am5.Legend.new(root, {
            x: am5.percent(50),
            centerX: am5.percent(50),
          })
        );
        legend.labels.template.setAll({ fill: textColor, fontSize: 12 });
        legend.valueLabels.template.setAll({ forceHidden: true });
        legend.data.setAll(chart.series.values);
      }

      chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none' }));
      chart.appear(500, 100);
    });
  }

  private setDynamicHeight(dataLength: number): void {
    const el = document.getElementById(this.chartId);
    if (!el) return;
    const groupSize = this.resolvedBars.length || 1;
    el.style.height = this.config.barHeight
      ? `${dataLength * this.config.barHeight * groupSize}px`
      : '350px';
  }

  ngOnDestroy(): void {
    this.barSeriesList = [];
    this.root?.dispose();
  }
}
